import React from "react";
import { Check, Plus, X } from "lucide-react";
import { aliasChanges, filterAliasAccounts, normalizeAliasIdentity } from "../../utils/billingAliases.js";

const BUTTON = "inline-flex min-h-8 items-center justify-center gap-1 rounded-[4px] border px-2 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";
const SECONDARY = `${BUTTON} border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900`;
const INPUT = "h-9 w-full min-w-0 rounded-[4px] border border-slate-300 bg-white px-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/15";

// Liste « Associer à cette entreprise » de la fiche : un clic ajoute ou retire un client.
function AliasPicker({ account, snapshot, owners, onAdd, inputRef, searchValue, onSearchChange }) {
  const accountNames = new Map(snapshot.accounts.map((row) => [row.id, row.name]));
  const needle = normalizeAliasIdentity(searchValue);
  const options = snapshot.entries.filter((row) => normalizeAliasIdentity(`${row.label} ${row.detail}`).includes(needle));
  return <div className="flex min-h-0 flex-1 flex-col bg-white" aria-label={`Ajouter un client à ${account.name}`}>
    <div className="shrink-0 border-b border-slate-200 p-2">
      <input ref={inputRef} className={INPUT} aria-label="Rechercher un client Data Manager"
        value={searchValue} placeholder="Nom ou code…" onChange={(event) => onSearchChange(event.target.value)} />
    </div>
    <ul className="!m-0 min-h-0 flex-1 !list-none overflow-y-auto !p-0">
      {options.map((row) => {
        const selected = owners[row.key] === account.id;
        const unavailable = Boolean(owners[row.key]) && !selected;
        return <li key={row.key} className="border-b border-slate-100 last:border-0">
          <button type="button" disabled={unavailable} aria-pressed={selected} className={`flex w-full items-start gap-2 rounded-[4px] px-2 py-2 text-left text-xs hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:text-slate-400 ${selected ? "bg-slate-100" : ""}`}
            onClick={() => { onAdd(row.key); inputRef.current?.focus(); }}>
            {selected ? <Check className="mt-0.5 shrink-0 text-accent" size={14} /> : <Plus className="mt-0.5 shrink-0" size={14} />}
            <span className="min-w-0 break-words"><strong>{row.label}</strong><span className="block">{row.detail}</span>
              {unavailable ? <span className="block">Déjà associé à {accountNames.get(owners[row.key]) || row.billing_account_name || "une autre entreprise"}</span> : null}
            </span>
          </button>
        </li>;
      })}
      {!options.length ? <li className="px-1 py-2 text-xs text-slate-500">Aucun élément disponible pour cette recherche.</li> : null}
    </ul>
  </div>;
}

export function BillingAssociationPicker({ accountId, draft, onDraftChange, focusKey = 0, busy }) {
  const inputRef = React.useRef(null);
  const [search, setSearch] = React.useState("");
  React.useEffect(() => {
    if (focusKey) { inputRef.current?.focus(); inputRef.current?.scrollIntoView({ block: "nearest" }); }
  }, [focusKey]);
  const account = draft.snapshot.accounts.find((row) => row.id === accountId);
  if (!account) return null;
  return <fieldset disabled={busy} aria-label="Liste des clients à associer"
    className="flex h-[28rem] min-h-0 min-w-0 flex-col overflow-hidden rounded-[4px] border border-slate-200 lg:h-full">
    <div className="shrink-0 border-b border-slate-200 p-2">
      <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-500">Associer à cette entreprise</h3>
      <p className="mt-1 text-[11px] text-slate-600">Sélectionnez les clients à rattacher. Les changements seront appliqués avec Enregistrer.</p>
    </div>
    <AliasPicker inputRef={inputRef} account={account} snapshot={draft.snapshot} owners={draft.owners}
      searchValue={search} onSearchChange={setSearch}
      onAdd={(key) => onDraftChange((current) => ({ ...current,
        owners: { ...current.owners, [key]: current.owners[key] === accountId ? null : accountId } }))} />
    <p className="shrink-0 border-t border-slate-200 px-2 py-1 text-xs text-slate-600">
      {draft.snapshot.entries.filter((row) => draft.owners[row.key] === accountId).length} sélectionné(s)
    </p>
  </fieldset>;
}

// Clients rattachés de la fiche ouverte ; « Ajouter » mène à la liste « Associer à cette entreprise ».
export default function BillingAssociationsEditor({ accountId, canEdit, busy = false, draft, onDraftChange, onPick }) {
  const changes = aliasChanges(draft.snapshot, draft.owners);
  const locked = busy || !canEdit;
  const changeOwner = (key, owner) => onDraftChange((current) => ({ ...current, owners: { ...current.owners, [key]: owner } }));
  const rows = filterAliasAccounts(draft.snapshot, draft.owners, "", { accountId, includeUnlinked: true });
  return <section aria-label="Associations des entreprises" className="flex min-h-0 flex-1 flex-col">
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 p-3">
      <p className="text-xs text-slate-600">Clients rattachés à cette fiche. Une association ne garantit pas que toutes les conditions de facturation sont réunies.</p>
    </div>
    <div className="min-h-0 flex-1 overflow-auto">
      <table className="!min-w-0 w-full table-fixed text-left text-xs" aria-label="Associations par entreprise">
        <colgroup className="hidden sm:table-column-group"><col className="w-full" /></colgroup>
        <thead className="sticky top-0 z-10 hidden bg-slate-100 sm:table-header-group"><tr>
          <th className="break-words border-b px-2 py-2">Clients Data Manager associés</th>
        </tr></thead>
        <tbody className="block sm:table-row-group">{rows.map((account, index) => {
          const associated = draft.snapshot.entries.filter((row) => draft.owners[row.key] === account.id);
          return <tr key={account.id} className={`block sm:table-row ${index % 2 ? "bg-slate-50/60" : "bg-white"}`}>
            <td className="block border-b border-slate-200 px-2 py-2 align-top sm:table-cell">
              <h3 className="mb-1 text-xs font-semibold text-slate-600 sm:hidden">Clients Data Manager associés</h3>
              <ul className="!m-0 flex !list-none flex-wrap gap-1 !p-0">{associated.map((row) => <li key={row.key} className="flex max-w-full items-start gap-1 rounded-[4px] border border-slate-200 bg-white px-2 py-1">
                <span className="min-w-0 break-words"><strong>{row.label}</strong><span className="block text-slate-500">{row.detail}</span>
                  {row.owner !== account.id ? <span className="block text-accent">À ajouter</span> : null}
                </span>
                {canEdit ? <button type="button" disabled={locked} aria-label={`Retirer ${row.label} de ${account.name}`}
                  className="shrink-0 rounded-[4px] p-1 text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:text-slate-400"
                  onClick={() => changeOwner(row.key, null)}><X size={13} /></button> : null}
              </li>)}</ul>
              {!associated.length ? <p className="mb-1 text-slate-400">Aucun client associé.</p> : null}
              {changes.filter((row) => row.owner === account.id && draft.owners[row.key] !== account.id).map((row) => <p key={row.key} className="mt-1 break-words text-slate-500">À retirer : {row.label} · {row.detail}</p>)}
              {canEdit ? <button type="button" className={`${SECONDARY} mt-1`} disabled={locked}
                aria-label={`Ajouter un client à ${account.name}`} onClick={() => onPick?.()}><Plus size={12} />Ajouter</button> : null}
            </td>
          </tr>;
        })}</tbody>
      </table>
      {!rows.length ? <p className="p-4 text-center text-sm text-slate-500">Cette fiche n’est plus active ou disponible. Fermez le panneau pour revenir au répertoire.</p> : null}
    </div>
  </section>;
}
